import { addDays, type DateArg, type Day, formatISO, format, lightFormat, startOfWeek } from 'date-fns';
import { TZDate } from '@date-fns/tz';

const DAYS_IN_WEEK = 7;

/**
 * Format a date using Intl.DateTimeFormat with specified options
 * @param date - The date to format
 * @param locale - The locale to use for formatting
 * @param options - DateTimeFormat options
 * @returns Formatted date string
 */
export function formatDate(date: Date, locale: string, options?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * Get the list of 7 days of the week, translated for the given locale
 * @param locale - The locale to use for translation
 * @param weekStart - The day to start the week (0=Sunday, 1=Monday, etc.)
 * @returns Array of localized weekday names
 */
export function getWeekdays(locale: string, weekStart: Day = 0): string[] {
  // Start from a known Sunday (August 1, 2021) and get the correct week start
  const referenceDate = startOfWeek(new Date(), { weekStartsOn: weekStart });

  return Array.from({ length: DAYS_IN_WEEK }, (_void, i) => {
    const date = addDays(referenceDate, i);
    return date.toLocaleDateString(locale, { weekday: 'short' });
  });
}

export function getTimezoneDate(date: Date, timezone: string): TZDate {
  return new TZDate(date, timezone);
}

/** Take a localized date and convert it to a UTC TZDate, overriding the timezone */
export function setTimezoneToUTC(date: Date): TZDate {
  return new TZDate(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds(), 'Etc/UTC');
}

/** Returns the date in ISO format (without time): YYYY-MM-DD */
export function getIsoDate(date: DateArg<Date>): string {
  return formatISO(date, { representation: 'date' });
}

/** Returns the date in ISO format (with time): YYYY-MM-DDTHH:mm:ss.sssZ */
export function getIsoDateTime(date: DateArg<Date>): string {
  return formatISO(date, { representation: 'complete' });
}

/** Returns the year-month key in "yyyy-MM" format, as seen in the given timezone */
export function getYearMonth(date: DateArg<Date>, timezone = 'Etc/UTC'): string {
  return lightFormat(getTimezoneDate(new Date(date as Date), timezone), 'yyyy-MM');
}

/** Returns the date in YYYY/MM/DD HH:mm:ss format */
export function getSlashDateTime(date: Date): string {
  return format(date, 'yyyy/MM/dd HH:mm:ss');
}
